import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { HttpResourceRef } from '@angular/common/http';
import { Observable, fromEvent, of } from 'rxjs';
import { catchError, filter, finalize, first, take, tap } from 'rxjs/operators';

import { BaseComponent } from '../../_core/components/base.component';
import { AuthRequestModel } from '../../_sdk/auth/auth.model';
import { AuthService } from '../../_services/auth/auth.service';
import { getExternalUrl } from '../../_core/url.utils';
import { UserService } from '../../_services/users/user.service';
import { LoadingIndicatorDirective } from '../../_modules/common-ui/loading-indicator/loading-indicator.directive';
import { ToastrService } from '../../_modules/toastr/toastr.service';

export type LoginStep = 'email' | 'password';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent extends BaseComponent {

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly toastrService = inject(ToastrService);
    private readonly userService = inject(UserService);

    private readonly loadingIndicator = viewChild(LoadingIndicatorDirective);

    public readonly emailForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email])
    });

    public readonly passwordForm = new FormGroup({
        password: new FormControl('', [Validators.required])
    });

    public readonly currentStep = signal<LoginStep>('email');
    public readonly processing = signal(false);
    public readonly actualPassword = signal('');

    private readonly _verifyEmailResource = signal<HttpResourceRef<any> | null>(null);

    public readonly nextButtonText = computed(() =>
        this.processing() ? 'Checking...' : 'Next'
    );

    public readonly loginButtonText = computed(() =>
        this.processing() ? 'One moment...' : 'Login'
    );

    constructor() {
        super();

        effect(() => {
            const resource = this._verifyEmailResource();
            if (!resource || resource.isLoading()) return;

            if (resource.error()) {
                this.processing.set(false);
                return;
            }
            const collection = resource.value();
            if (!collection?.any()) {
                this.processing.set(false);
                return;
            }

            const user = collection.data[0] as any;

            if (user?.mode === 'sso' && user?.redirectUrl) {
                window.location.href = user.redirectUrl;
                return;
            }

            this.currentStep.set('password');
            this.processing.set(false);
        });
    }

    OnInit(): void {
        if (this.authService.consumeSessionExpiredNotice()) {
            this.showSessionExpiredNotice();
        }

        // Someone who arrives already signed in did not just enter a password, so nothing has written
        // a fresh page-auth cookie for Report Center yet.
        let signedInOnArrival = !String.isNullOrEmpty(this.authService.getCurrentToken());
        let leaving = false;

        // Another tab may log in while this one is still sitting on /login — the token and user it
        // writes reach this tab via the storage event, but only if something here is actually still
        // listening for it (the checks below only run once, at init). Wait for the USER (not just the
        // token) to show up: login stores them as two separate writes, and navigating as soon as the
        // token alone appears races PermissionGuard against a still-stale cached user with no
        // permissions yet, which reads as an access denial instead of "not ready yet".
        this.addSubscription(
            this.authService.authenticatedUser$.pipe(
                filter(user => !!user?.UserGuid)
            ),
            () => {
                if (leaving) {
                    return;
                }
                // Report Center sends people here with externalUrl when their session has lapsed, and
                // AuthService.handleLoggedIn() sends them back to it. This navigation fires on the same
                // sign-in, so it has to agree: otherwise whichever runs last decides where the user
                // lands, and a user who came from a report ends up on the dashboard.
                const externalUrl = getExternalUrl(this.route);
                if (externalUrl && signedInOnArrival) {
                    // Report Center reads only the page-auth cookie, which lapses with the access token
                    // and is written only by a login or a token refresh. Sending an already signed-in user
                    // straight back would bounce them between the report and this page, so refresh first.
                    leaving = true;
                    // The page's usual busy state: the button reads "Checking..." and the form is disabled.
                    this.processing.set(true);
                    this.addSubscription(this.authService.refreshAccessToken().pipe(take(1)),
                        () => this.router.navigate(['/externalRedirect', { externalUrl }]),
                        () => {
                            // The session is really over: expiry handling has cleared it and left the
                            // sign-in form up, with externalUrl intact for the login that follows.
                            signedInOnArrival = false;
                            leaving = false;
                            this.processing.set(false);
                        });
                    return;
                }
                if (externalUrl) {
                    leaving = true;
                    this.router.navigate(['/externalRedirect', { externalUrl }]);
                    return;
                }

                const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
                if (returnUrl) {
                    this.router.navigateByUrl(decodeURIComponent(returnUrl));
                } else {
                    // Same home page as a password sign-in, chosen by role, instead of the empty /admin start page.
                    this.authService.navigateToHome();
                }
            }
        );

       this.addSubscription(
            this.route.queryParamMap.pipe(
                first(),
                tap((params) => {
                    if (params.get('saml') === 'yes') {
                        this.processing.set(true);
                        const token = this.getCookie();
                        if (!token) {
                            this.processing.set(false);
                            this.router.navigate(['/login']);
                        } else {
                            this.addSubscription(
                                this.process(
                                    this.authService.loginSaml(token).pipe(
                                        take(1),
                                        finalize(() => this.processing.set(false))
                                    )
                                )
                            );
                        }
                    }
                    // else {
                    //     // If the user lands on /login with a valid refresh cookie, restore the session
                    //     // instead of making them log in again after an app/API restart.
                    //     this.addSubscription(
                    //         this.authService.ensureAuthenticated()
                    //             .pipe(
                    //                 take(1),
                    //                 tap(isAuthenticated => {
                    //                     if (isAuthenticated) {
                    //                         const returnUrl = params.get('returnUrl');
                    //                         this.router.navigateByUrl(returnUrl ? decodeURIComponent(returnUrl) : '/admin');
                    //                     }
                    //                 })
                    //             )
                    //     );
                    // }
                })
            )
        );
    }

    //  A tab can expire while it's in the background (idle sign-out); wait until the user is back on it,
    //  otherwise the toast would close before anyone saw it.
    private showSessionExpiredNotice(): void {
        const show = () => this.toastrService.warn('Your session has expired. Please sign in again.');
        if (document.visibilityState === 'visible') {
            show();
            return;
        }
        this.addSubscription(
            fromEvent(document, 'visibilitychange').pipe(
                filter(() => document.visibilityState === 'visible'),
                take(1)
            ),
            show
        );
    }

    public onNext(): void {
        this.emailForm.markAllAsTouched();

        if (this.emailForm.invalid) {
            return;
        }

        const email = this.emailForm.value.email!.trim();
        this.processing.set(true);

        this._verifyEmailResource.set(this.authService.verifyEmail(email));
    }

    public onLogin(): void {
        this.passwordForm.markAllAsTouched();

        if (this.passwordForm.invalid) {
            return;
        }

        this.processing.set(true);

        const request = {
            Email: this.emailForm.value.email!.trim(),
            Password: this.actualPassword(),
        } as AuthRequestModel;

        this.addSubscription(
            this.process(
                this.authService.login(request).pipe(
                    take(1),
                    finalize(() => this.processing.set(false)),
                    catchError(() => {
                        return of(null);
                    })
                )
            )
        );
    }

    public onBack(): void {
        this.currentStep.set('email');
        this.passwordForm.reset();
        this.actualPassword.set('');
    }

    public onPasswordAutofill(event: Event): void {
        const password = (event.target as HTMLInputElement).value;
        this.actualPassword.set(password);
        this.passwordForm.get('password')?.setValue(password);
    }

    public onForgotPassword(): void {
        const email = this.emailForm.value.email?.trim();
        if (!email) {
            return;
        }

        if (confirm(`Do you want to send a recovery email to '${email}'?`)) {
            this.addSubscription(
                this.process(this.userService.requestPasswordRecovery(email))
            );
        }
    }

    private process<T>(stream: Observable<T>): Observable<T> {
        const indicator = this.loadingIndicator();
        return indicator ? indicator.process(stream) : stream;
    }

    private getCookie(): string | null {
        return document.cookie
            .split(';')
            .find(c => c.includes('access_token'))
            ?.replace(/^[^=]+=/, '') ?? null;
    }
}
